import { useCallback, useEffect, useState } from "react"
import { ArrowLeft, ArrowUpRight, Ban, CheckCircle2, KeyRound, LoaderCircle, MoreHorizontal, Pencil, ShieldCheck, Trash2 } from "lucide-react"
import { Link, Navigate } from "react-router-dom"
import { toast } from "sonner"
import { AppShell } from "@/components/app-shell"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { api, type ManagedAccount } from "@/lib/api"

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short", year: "numeric" }).format(date)
}

export function AccountsPage() {
  const [authorized, setAuthorized] = useState<"checking" | "yes" | "no">("checking")
  const [currentUserId, setCurrentUserId] = useState<string>()
  const [accounts, setAccounts] = useState<ManagedAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string>()
  const [resetTarget, setResetTarget] = useState<ManagedAccount | null>(null)
  const [newPassword, setNewPassword] = useState("")
  const [resetting, setResetting] = useState(false)
  const [editTarget, setEditTarget] = useState<ManagedAccount | null>(null)
  const [editName, setEditName] = useState("")
  const [editRole, setEditRole] = useState<"admin" | "superadmin">("admin")
  const [editing, setEditing] = useState(false)
  const [banTarget, setBanTarget] = useState<ManagedAccount | null>(null)
  const [banReason, setBanReason] = useState("")
  const [banning] = useState(false)
  const [actingId, setActingId] = useState<string>()

  const load = useCallback(async () => {
    setLoading(true); setLoadError(undefined)
    try { setAccounts(await api.listUsers()) }
    catch (error) { setLoadError(error instanceof Error ? error.message : "Não foi possível carregar as contas.") }
    finally { setLoading(false) }
  }, [])

  useEffect(() => {
    api.me()
      .then((user) => {
        setCurrentUserId(user.id)
        setAuthorized(user.role === "superadmin" ? "yes" : "no")
      })
      .catch(() => setAuthorized("no"))
  }, [])

  useEffect(() => { if (authorized === "yes") void load() }, [authorized, load])

  async function submitReset() {
    if (!resetTarget || newPassword.length < 8) return
    setResetting(true)
    try {
      await api.setUserPassword(resetTarget.id, newPassword)
      toast.success(`Senha redefinida para ${resetTarget.email}.`)
      setResetTarget(null); setNewPassword("")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível redefinir a senha.")
    } finally { setResetting(false) }
  }

  function openEdit(account: ManagedAccount) {
    setEditTarget(account)
    setEditName(account.name)
    setEditRole(account.role === "superadmin" ? "superadmin" : "admin")
  }

  async function submitEdit() {
    if (!editTarget || editName.trim().length < 2) return
    setEditing(true)
    try {
      if (editName.trim() !== editTarget.name) await api.updateUserName(editTarget.id, editName.trim())
      if (editRole !== (editTarget.role === "superadmin" ? "superadmin" : "admin")) {
        if (editTarget.id === currentUserId) {
          toast.error("Você não pode rebaixar a própria conta de superadmin.")
          return
        }
        await api.setUserRole(editTarget.id, editRole)
      }
      toast.success(`Conta ${editTarget.email} atualizada.`)
      setEditTarget(null)
      await load()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível salvar a conta.")
    } finally { setEditing(false) }
  }

  async function submitBanToggle(account: ManagedAccount) {
    const isBanned = Boolean(account.banned)
    if (!isBanned && account.id === currentUserId) {
      toast.error("Você não pode desativar a própria conta.")
      return
    }
    setActingId(account.id)
    try {
      if (isBanned) {
        await api.unbanUser(account.id)
        toast.success(`${account.email} reativada.`)
      } else {
        await api.banUser(account.id, banReason)
        toast.success(`${account.email} desativada.`)
      }
      setBanTarget(null); setBanReason("")
      await load()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível alterar o status da conta.")
    } finally { setActingId(undefined) }
  }

  async function submitRemove(account: ManagedAccount) {
    if (account.id === currentUserId) {
      toast.error("Você não pode remover a própria conta.")
      return
    }
    if (!window.confirm(`Remover ${account.email}? Os assistentes dela serão excluídos em cascata. Essa ação não pode ser desfeita.`)) return
    setActingId(account.id)
    try {
      await api.removeUser(account.id)
      toast.success(`${account.email} removida.`)
      await load()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível remover a conta.")
    } finally { setActingId(undefined) }
  }

  if (authorized === "checking") {
    return <AppShell><div className="flex min-h-[60vh] items-center justify-center"><LoaderCircle className="size-5 animate-spin text-primary" /></div></AppShell>
  }
  if (authorized === "no") return <Navigate to="/" replace />

  return <AppShell>
    <div className="relative min-h-[calc(100vh-82px)]">
      <div className="relative mx-auto max-w-[1000px] px-5 py-8 sm:px-8 xl:py-10">
        <nav aria-label="Navegação" className="muted mb-4 flex items-center gap-1.5 text-[12px]">
          <Link to="/" className="font-semibold hover:text-foreground hover:underline">Painel</Link>
          <span aria-hidden="true">/</span>
          <span className="text-foreground">Contas</span>
        </nav>
        <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <section>
            <p className="muted mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em]"><ShieldCheck className="size-3.5" />Administração · Superadmin</p>
            <h1 className="font-display text-[34px] font-bold leading-tight tracking-[-0.05em] sm:text-[38px]">Contas</h1>
            <p className="muted mt-2 max-w-[640px] text-[14px]">Todas as contas cadastradas no BubbleDesk. Edite o nome, troque o papel, desative o acesso ou redefina a senha. Contas desativadas não conseguem entrar, mas mantêm os dados.</p>
          </section>
          <Button asChild variant="outline" className="h-11 shrink-0 gap-1.5 rounded-xl border-border px-4 text-[13px] font-semibold">
            <Link to="/"><ArrowLeft className="size-4" />Voltar ao painel</Link>
          </Button>
        </div>

        <section className="bg-card rounded-2xl border border-border">
          <div className="hidden grid-cols-[1.6fr_1fr_1fr_220px] gap-4 border-b border-border px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.13em] muted sm:grid">
            <span>Conta</span><span>Papel / status</span><span>Criada em</span><span className="text-right">Ações</span>
          </div>
          {loadError ? (
            <div className="p-6 text-[13px] text-danger">{loadError}</div>
          ) : loading ? (
            <div className="space-y-2 p-6">{[1, 2, 3].map((item) => <Skeleton key={item} className="h-14 field-bg" />)}</div>
          ) : accounts.length ? (
            accounts.map((account) => {
              const isSelf = account.id === currentUserId
              const isBanned = Boolean(account.banned)
              return (
                <article key={account.id} className="grid grid-cols-1 gap-3 border-b border-border px-5 py-4 last:border-b-0 sm:grid-cols-[1.6fr_1fr_1fr_220px] sm:items-center sm:gap-4 sm:px-6">
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-semibold">{account.name}{isSelf && <span className="muted ml-2 text-[11px] font-normal">(você)</span>}</p>
                    <p className="muted mt-0.5 truncate text-[11px]">{account.email}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={`w-fit rounded-full px-2.5 py-1 text-[11px] font-medium ${account.role === "superadmin" ? "border border-info/30 bg-info/10 text-info" : "border border-border bg-muted text-muted-foreground"}`}>{account.role === "superadmin" ? "Superadmin" : "Admin"}</span>
                    {isBanned && <span className="w-fit rounded-full border border-danger/30 bg-danger/10 px-2.5 py-1 text-[11px] font-medium text-danger">Desativada</span>}
                  </div>
                  <span className="muted text-[12px]">{formatDate(account.createdAt)}</span>
                  <div className="flex flex-nowrap items-center justify-start gap-2 sm:justify-end">
                    <Button variant="outline" size="sm" title="Editar nome e papel" className="h-8 shrink-0 gap-1.5 rounded-lg border-border px-2.5 text-[12px]" onClick={() => openEdit(account)}><Pencil className="size-3.5" />Editar</Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" title={`Mais ações para ${account.name}`} aria-label={`Mais ações para ${account.name}`} className="size-8 shrink-0 rounded-lg border-border text-muted-foreground hover:text-foreground">
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[196px]">
                        <DropdownMenuItem onClick={() => { setResetTarget(account); setNewPassword("") }}>
                          <KeyRound className="size-4" />Redefinir senha
                        </DropdownMenuItem>
                        {isBanned ? (
                          <DropdownMenuItem disabled={actingId === account.id} className="text-success focus:text-success" onClick={() => void submitBanToggle(account)}>
                            <CheckCircle2 className="size-4" />Reativar acesso
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem disabled={isSelf || actingId === account.id} title={isSelf ? "Você não pode desativar a própria conta" : undefined} onClick={() => { setBanTarget(account); setBanReason("") }}>
                            <Ban className="size-4" />Desativar acesso
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem disabled={isSelf || actingId === account.id} title={isSelf ? "Você não pode remover a própria conta" : undefined} className="text-danger focus:text-danger" onClick={() => void submitRemove(account)}>
                          <Trash2 className="size-4" />Excluir conta
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </article>
              )
            })
          ) : (
            <div className="px-6 py-12 text-center text-[13px] muted">Nenhuma conta encontrada.</div>
          )}
        </section>
      </div>
    </div>

    <Dialog open={Boolean(resetTarget)} onOpenChange={(open) => { if (!open) { setResetTarget(null); setNewPassword("") } }}>
      <DialogContent className="bg-card rounded-2xl border-border p-6 shadow-2xl sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="font-display text-[21px]">Redefinir senha</DialogTitle>
          <DialogDescription className="text-muted-foreground text-[13px]">Nova senha para <strong>{resetTarget?.email}</strong>. Avise a pessoa por fora, o BubbleDesk não envia e-mail nesse fluxo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-3">
          <Label htmlFor="new-account-password" className="text-[13px] font-medium">Nova senha</Label>
          <Input id="new-account-password" type="text" autoFocus placeholder="Mínimo 8 caracteres" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void submitReset() }} className="field-bg h-12 rounded-xl border-border text-[14px]" />
          {newPassword.length > 0 && newPassword.length < 8 && <p className="text-[12px] text-danger">Mínimo de 8 caracteres.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="h-11 rounded-xl border-border" onClick={() => setResetTarget(null)}>Cancelar</Button>
          <Button className="accent-bg h-11 rounded-xl" onClick={() => void submitReset()} disabled={resetting || newPassword.length < 8}>{resetting && <LoaderCircle className="size-4 animate-spin" />}{resetting ? "Redefinindo..." : "Redefinir senha"}<ArrowUpRight className="size-4" /></Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={Boolean(editTarget)} onOpenChange={(open) => { if (!open) setEditTarget(null) }}>
      <DialogContent className="bg-card rounded-2xl border-border p-6 shadow-2xl sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="font-display text-[21px]">Editar conta</DialogTitle>
          <DialogDescription className="text-muted-foreground text-[13px]">{editTarget?.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="space-y-2">
            <Label htmlFor="edit-account-name" className="text-[13px] font-medium">Nome</Label>
            <Input id="edit-account-name" value={editName} onChange={(event) => setEditName(event.target.value)} className="field-bg h-12 rounded-xl border-border text-[14px]" />
            {editName.trim().length > 0 && editName.trim().length < 2 && <p className="text-[12px] text-danger">Mínimo de 2 caracteres.</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-account-role" className="text-[13px] font-medium">Papel</Label>
            <Select value={editRole} onValueChange={(next) => setEditRole(next as "admin" | "superadmin")}>
              <SelectTrigger id="edit-account-role" className="field-bg h-12 rounded-xl border-border text-[14px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin — workspace próprio</SelectItem>
                <SelectItem value="superadmin">Superadmin — vê todos os tenants</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="h-11 rounded-xl border-border" onClick={() => setEditTarget(null)}>Cancelar</Button>
          <Button className="accent-bg h-11 rounded-xl" onClick={() => void submitEdit()} disabled={editing || editName.trim().length < 2}>{editing && <LoaderCircle className="size-4 animate-spin" />}{editing ? "Salvando..." : "Salvar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={Boolean(banTarget)} onOpenChange={(open) => { if (!open) { setBanTarget(null); setBanReason("") } }}>
      <DialogContent className="bg-card rounded-2xl border-border p-6 shadow-2xl sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="font-display text-[21px]">Desativar conta</DialogTitle>
          <DialogDescription className="text-muted-foreground text-[13px]"><strong>{banTarget?.email}</strong> perde o acesso imediatamente, mas os dados são mantidos. Você pode reativar depois.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-3">
          <Label htmlFor="ban-reason" className="text-[13px] font-medium">Motivo (opcional)</Label>
          <Input id="ban-reason" placeholder="ex.: acesso temporariamente suspenso" value={banReason} onChange={(event) => setBanReason(event.target.value)} className="field-bg h-12 rounded-xl border-border text-[14px]" />
        </div>
        <DialogFooter>
          <Button variant="outline" className="h-11 rounded-xl border-border" onClick={() => setBanTarget(null)}>Cancelar</Button>
          <Button className="h-11 rounded-xl bg-danger text-danger-foreground hover:bg-danger/90" onClick={() => banTarget && void submitBanToggle(banTarget)} disabled={banning || actingId === banTarget?.id}>{actingId === banTarget?.id && <LoaderCircle className="size-4 animate-spin" />}Desativar conta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </AppShell>
}
